import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { useExamplePreview } from 'hooks/useExamplePreview';

import { ReadOnlyTableRow } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/ReadOnlyTableRow';
import { TableSectionTitle } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TableSectionTitle';
import { TotalRowComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TotalRowComponent';
import { getEachRowValues } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetSalesForm/helpers';
import { GLOBAL_PERIOD_NAME } from 'components/BusinessToolForm/Sections/Global/form';

import { concatWithDot } from 'helpers/string';

import { IOperatingActivitiesProps } from './types';
import { OPERATING_ACTIVITIES_NAMES, useGetOperatingActivitiesTableHeadings } from './utils/form';
import { FIRST_COLUMN_WIDTH, operatingActivitiesPrefix } from '../../../../utils/form';

export const OperatingActivities: FC<IOperatingActivitiesProps> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const { isExamplePage} = useExamplePreview()
  const { operatingActivitiesFields, totalRowTitle } = useGetOperatingActivitiesTableHeadings(isExamplePage);

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const cashReceivedFromCustomers = getValues(
    concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_RECEIVED_CUSTOMERS),
  );
  const cashPaidSuppliers = getValues(
    concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_PAID_SUPPLIERS),
  );
  const cashPaidEmployees = getValues(
    concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_PAID_EMPLOYEES),
  );
  const cashPaidOtherOperatingExpensesValue = getValues(
    concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_PAID_OTHER_OPERATING_EXPENSES),
  );
  const interestReceivedValue = getValues(
    concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INTEREST_RECEIVED),
  );
  const interestPaid = getValues(concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INTEREST_PAID));
  const incomeTaxesPaidValue = getValues(
    concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INCOME_TAXES_PAID),
  );
  const operatingActivitiesNetCash = getValues(
    concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.NET_CASH_OPERATING_ACTIVITIES),
  );

  return (
    <Grid container direction={'column'}>
      <Grid item xs>
        <TableSectionTitle title={t('financialPlan.projectedCashFlow.direct.operatingActivities.title')} />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_RECEIVED_CUSTOMERS)}
          columnValues={getEachRowValues([cashReceivedFromCustomers], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={operatingActivitiesFields[OPERATING_ACTIVITIES_NAMES.CASH_RECEIVED_CUSTOMERS].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_PAID_SUPPLIERS)}
          columnValues={getEachRowValues([cashPaidSuppliers], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={operatingActivitiesFields[OPERATING_ACTIVITIES_NAMES.CASH_PAID_SUPPLIERS].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_PAID_EMPLOYEES)}
          columnValues={getEachRowValues([cashPaidEmployees], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={operatingActivitiesFields[OPERATING_ACTIVITIES_NAMES.CASH_PAID_EMPLOYEES].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.CASH_PAID_OTHER_OPERATING_EXPENSES)}
          columnValues={getEachRowValues([cashPaidOtherOperatingExpensesValue], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={operatingActivitiesFields[OPERATING_ACTIVITIES_NAMES.CASH_PAID_OTHER_OPERATING_EXPENSES].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INTEREST_RECEIVED)}
          columnValues={getEachRowValues([interestReceivedValue], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={operatingActivitiesFields[OPERATING_ACTIVITIES_NAMES.INTEREST_RECEIVED].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INTEREST_PAID)}
          columnValues={getEachRowValues([interestPaid], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={operatingActivitiesFields[OPERATING_ACTIVITIES_NAMES.INTEREST_PAID].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INCOME_TAXES_PAID)}
          columnValues={getEachRowValues([incomeTaxesPaidValue], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={operatingActivitiesFields[OPERATING_ACTIVITIES_NAMES.INCOME_TAXES_PAID].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs>
        <TotalRowComponent
          name={concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.NET_CASH_OPERATING_ACTIVITIES)}
          columnValues={getEachRowValues([operatingActivitiesNetCash], 0, period)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          totalRowTitle={totalRowTitle}
          disabled={disabled}
          marginBottomSize="none"
        />
      </Grid>
    </Grid>
  );
};
